import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllTelephonyDetails,
  setTelephonyExtensions,
  telephonyExtensionsSelector,
} from '../../../servicesSlice';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import { deleteTelephonyExtension } from '../../../../../utils/ServicesAPIUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import {
  TELEPHONY_EXTENSIONS_CREATE,
  TELEPHONY_EXTENSIONS_EDIT,
} from '../../../../../constants/ServiceConstants';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_SERVICES_TELEPHONY,
} from '../../../../../constants/RoleConsts';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import {
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Checkbox,
} from '@material-ui/core';
import Dialog from '@mui/material/Dialog';
import { AddCircle, DragIndicator, MoreHoriz } from '@material-ui/icons';
import CreateIcon from '@material-ui/icons/Create';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import TablePaginationActions from '../../../../../shared/table-pagination/TablePaginationActions';
import SearchIcon from '@material-ui/icons/Search';
import ExtensionUpload from '../extension-upload/Extension-Upload';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';
import { handleOnDragEnd } from '../../../../../shared/custom-dragdrop/dragDrop';
import { SaveButton } from '../../../../../shared/custom-dragdrop/Save-Button';
import { createDataSortMapping } from '../../../../../utils/PropertyAPIUtils';
import Modal from '@material-ui/core/Modal';
import { makeStyles } from '@material-ui/core/styles';
import { CSVLink } from 'react-csv';
import MultipleDeleteConfirmationPopup from '../../../../../shared/MultiActionPopUp/MultiDeleteConfirmationPopup';
import SearchBar from '../../../../../shared/search-bar/Search-Bar';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';

function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}

const useStyles = makeStyles((theme) => ({
  paper: {
    position: 'absolute',
    width: 400,
    backgroundColor: theme.palette.background.paper,
    border: 'none',
    borderRadius: '10px',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
}));

const ExtensionsList = (props) => {
  const { onDrawerToggle, hotelId, permissions, page, rowsPerPage, onsetRowsPerPage, onsetPage } =
    props;
  const dispatch = useDispatch();
  const originalExtensionsList = useSelector(telephonyExtensionsSelector);
  const [extensionsList, setExtensionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadState, setUploadState] = useState(false);
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
    extension: null,
  });
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef(null);
  const prevHotelIdRef = useRef(hotelId);
  const [popUp, setPopUp] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dragDisabled = !!searchText || extensionsList?.length <= 1;
  const [searchButton, setSearchButton] = useState(true);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const classes = useStyles();
  const [modalStyle] = useState(getModalStyle);

  useEffect(() => {
    setExtensionsList(originalExtensionsList);
    setLoading(false);
    setSearchText('');
    setSearchButton(true);
  }, [originalExtensionsList]);

  useEffect(() => {
    if (loading || extensionsList?.length === 0) return;
    const totalItems = extensionsList?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [extensionsList?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== hotelId) {
      onsetPage(0);
    }
    prevHotelIdRef.current = hotelId;
  }, [hotelId]);

  const handleSearchText = async (searchText) => {
    setSearchText(searchText);
    const filteredRows = await originalExtensionsList.filter((extension) => {
      return extension.roomNumber.toLowerCase().includes(searchText.toLowerCase());
    });
    onsetPage(0);
    setExtensionsList(filteredRows);
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      extension: null,
    });
  };

  const handleCellMenuClick = (event, extension) => {
    setSelectedValue({
      id: extension.id,
      name: extension.roomNumber,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      extension,
    });
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onClickDelete = (event) => {
    setSelectedValue((prev) => ({
      ...prev,
      title: 'Are you sure you want to delete',
      description: 'This action cannot be reversed',
      status: true,
      anchorEl: null,
    }));
  };

  const onOpenDrawer = (event, type) => {
    event.preventDefault();
    onDrawerToggle(type, true, selectedValue.extension);
    handleClose();
  };

  const onDeleteCancel = (event) => {
    handleClose();
  };

  const onDeleteItem = async () => {
    try {
      dispatch(stepUpLoaderCount());
      if (hotelId && selectedValue.id) {
        const selectedLanguage = decryptLocalStorage('lang');
        const language = selectedLanguage !== 'en' ? selectedLanguage : '';
        const payload = {
          id: selectedValue.id,
          hotelId: hotelId,
        };
        await deleteTelephonyExtension(payload, language);
        dispatch(fetchAllTelephonyDetails(hotelId));
        notificationManager('success', 'Extension deleted successfully');
      }
      handleClose();
    } catch (e) {
      if (e.graphQLErrors) {
        notificationManager('danger', e.graphQLErrors[0].message);
      } else {
        notificationManager('danger', 'Failed to delete Extension');
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
    handleClose();
  };

  const onClickUpload = (e) => {
    setUploadState(true);
  };

  const handleUploadDialogClose = (e) => {
    setUploadState(false);
  };

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  const generateExportData = (data) => {
    const fields = ['roomNumber', 'extensionNumber', 'password'];
    return data.map((record) =>
      Object.fromEntries(
        Object?.entries(record)?.filter(([key, value]) =>
          fields?.includes(key) ? [key, "'" + value] : null,
        ),
      ),
    );
  };

  const headers = [
    { label: 'Room Number', key: 'roomNumber' },
    { label: 'Extension Number', key: 'extensionNumber' },
    { label: 'Password', key: 'password' },
  ];

  return (
    <div className='mt-4 mb-4 group-list-container'>
      <Dialog fullScreen open={uploadState} onClose={handleUploadDialogClose}>
        <ExtensionUpload onClose={handleUploadDialogClose} hotelId={hotelId} />
      </Dialog>
      {selectedValue.id && selectedValue.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={onDeleteItem}
          onDeleteCancel={onDeleteCancel}
        />
      )}
      <div className='row mx-2 d-flex'>
        <div className='col-12 col-lg-4'>
          <TextField
            className='w-100'
            onChange={(e) => {
              handleSearchText(e.target.value);
              setSearchButton(e?.target?.value ? false : true);
            }}
            label='Search'
            value={searchText}
            InputProps={
              searchButton && {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton className='showpopup'>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }
          />
        </div>

        <div className='col-12 col-lg-8 pt-3 pt-lg-0 d-flex justify-content-end'>
          <SaveButton
            visible={visible}
            setVisible={setVisible}
            popUp={popUp}
            setPopUp={setPopUp}
            placeHolder={extensionsList}
            setPlaceHolder={setExtensionsList}
            globalPlaceHolder={originalExtensionsList}
            setGlobalPlaceHolder={setTelephonyExtensions}
            dispatch={dispatch}
            mappingApi={createDataSortMapping}
            displayText='Telephony extensions'
            module='TELEPHONY_EXTENSION'
          />
          {permissions[RBAC_TYPE_SERVICES_TELEPHONY] === RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
            <>
              <div>
                <Button variant='outlined' size='large' className='me-4 customize-btn'>
                  <CSVLink
                    style={{ color: '#828282', textDecoration: 'none' }}
                    filename={`Elevate-telephony-extensions.csv`}
                    headers={headers}
                    data={
                      extensionsList && extensionsList.length !== 0
                        ? generateExportData(extensionsList)
                        : []
                    }
                    onClick={() => {
                      if (extensionsList.length === 0) {
                        setIsEmptyData(true);
                        return false;
                      }
                    }}
                  >
                    Export
                    <span>
                      <img
                        className='mb-1 ms-2'
                        src={'./assets/icons/dashboard/export-icon.svg'}
                        alt={'completed'}
                      />
                    </span>
                  </CSVLink>
                </Button>
              </div>
              <div>
                <Button
                  variant='outlined'
                  size='large'
                  onClick={(e) => {
                    onClickUpload(e);
                  }}
                  className='me-4 customize-btn showpopup'
                >
                  Upload
                  <span>
                    <img
                      className='mb-1 ms-2'
                      src={'./assets/icons/dashboard/upload-icon.svg'}
                      alt={'completed'}
                    />
                  </span>
                </Button>
              </div>
              <Button
                variant='contained'
                color='primary'
                size='large'
                startIcon={<AddCircle />}
                onClick={(e) => {
                  onOpenDrawer(e, TELEPHONY_EXTENSIONS_CREATE);
                }}
                className='showpopup'
              >
                Add Extension
              </Button>
            </>
          )}
        </div>
      </div>
      <div ref={wrapperRef} className='row mx-2 d-flex mt-3'>
        {loading ? (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        ) : (
          extensionsList && (
            <TableContainer>
              <Table className='list-table' aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell className='w-50'>Room Number</TableCell>
                    <TableCell className='w-30' align='right'>
                      Extension Number
                    </TableCell>
                    <TableCell className='w-20' align='right'>
                      Password
                    </TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>

                <DragDropContext
                  onDragEnd={(event) =>
                    handleOnDragEnd(
                      event,
                      setVisible,
                      extensionsList,
                      setExtensionsList,
                      false,
                      page,
                      rowsPerPage,
                    )
                  }
                  isDropDisabled={dragDisabled}
                >
                  <Droppable droppableId='services' isDropDisabled={dragDisabled}>
                    {(provided) => (
                      <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                        {(rowsPerPage > 0
                          ? extensionsList.slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage,
                            )
                          : extensionsList
                        ).map((extension, i) => (
                          <Draggable
                            key={extension.id}
                            draggableId={extension.id}
                            index={i}
                            isDragDisabled={dragDisabled}
                          >
                            {(provided) => (
                              <TableRow
                                key={i}
                                className='border-white'
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <TableCell>
                                  {!dragDisabled && (
                                    <DragIndicator className='res-menu-accordion-drag' />
                                  )}
                                </TableCell>
                                <TableCell className='w-50'>{extension?.roomNumber}</TableCell>
                                <TableCell className='w-30' align='right'>
                                  {extension?.extensionNumber}
                                </TableCell>
                                <TableCell className='w-20' align='right'>
                                  {extension?.password}
                                </TableCell>
                                <TableCell align='right'>
                                  {permissions[RBAC_TYPE_SERVICES_TELEPHONY] ===
                                    RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                                    <Button
                                      aria-controls='simple-menu'
                                      aria-haspopup='true'
                                      onClick={(e) => {
                                        handleCellMenuClick(e, extension);
                                      }}
                                      className='showpopupInner'
                                    >
                                      <MoreHoriz className='showpopupInner' />
                                    </Button>
                                  )}
                                  <Menu
                                    id={extension.id}
                                    anchorEl={selectedValue?.anchorEl}
                                    disableRestoreFocus
                                    MenuListProps={{ autoFocusItem: false }}
                                    keepMounted
                                    open={
                                      Boolean(selectedValue.anchorEl) &&
                                      selectedValue.id === extension.id
                                    }
                                    onClose={handleClose}
                                    className='shadow'
                                  >
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onOpenDrawer(e, TELEPHONY_EXTENSIONS_EDIT);
                                      }}
                                    >
                                      <CreateIcon className='me-2 action-menu-icon' />
                                      Edit
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onClickDelete(e);
                                      }}
                                    >
                                      <DeleteForeverIcon className='me-2 action-menu-icon' />
                                      Delete
                                    </MenuItem>
                                  </Menu>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>
                <TableFooter>
                  <TableRow className='paginator'>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      count={extensionsList.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )
        )}
      </div>
      {isEmptyData && (
        <Modal open={isEmptyData}>
          <div style={modalStyle} className={classes.paper}>
            <div className='custom-popup mt-4'>
              <div className='custom-popup-title'>There is no data to export</div>
              <div className='col-md-10 offset-1 d-flex justify-content-around px-4 mt-4'>
                <Button
                  variant='outlined'
                  size='large'
                  color='secondary'
                  onClick={() => setIsEmptyData(false)}
                >
                  Ok
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ExtensionsList;
